import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';

export type Environment = 'development' | 'staging' | 'production';

const ERROR_DETAIL_MAX_LENGTH = 200;

export interface UpdateSystemAddonsCommand {
  addons: string;
  insightResources?: string;
}

interface SystemAddonsResponse {
  addons: string;
}

interface ErrorResponse {
  message?: string;
  error?: string;
}

export class SystemAddonsService {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async safeJsonParse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isNoContent = response.status >= 204 && response.status <= 205;

    if (!contentType?.includes('application/json') || isNoContent) {
      throw new Error(
        `Unexpected response format: ${response.status} ${response.statusText}`,
      );
    }

    try {
      return (await response.json()) as T;
    } catch (parseError) {
      console.error('JSON parse failed:', parseError);
      throw new Error('Invalid response format from server');
    }
  }

  private async extractErrorDetail(response: Response): Promise<string> {
    const contentType = response.headers.get('content-type');

    if (!contentType?.includes('application/json')) {
      return response.statusText;
    }

    try {
      const errorBody = await this.safeJsonParse<ErrorResponse>(response);
      return errorBody.message || errorBody.error || response.statusText;
    } catch {
      return response.statusText;
    }
  }

  private async handleResponse<T>(response: Response): Promise<T | void> {
    if (response.status === 204) {
      return;
    }

    if (!response.ok) {
      const errorDetail = await this.extractErrorDetail(response);
      const truncatedDetail = errorDetail.substring(0, ERROR_DETAIL_MAX_LENGTH);

      console.error(`API request failed [${response.status}]:`, {
        status: response.status,
        statusText: response.statusText,
        detail: truncatedDetail,
      });

      throw new Error(
        `Request failed with status ${response.status}. Please check your permissions and try again.`,
      );
    }

    return this.safeJsonParse<T>(response);
  }

  private async buildBaseUrl(env: Environment): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('supply-features');
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${normalizedBase}/open-control/${env}`;
  }

  private extractSystemName(systemRef: string): string {
    return systemRef.split('/').pop()?.replace('system:', '') || systemRef;
  }

  private async getAuthHeaders(): Promise<Headers> {
    try {
      const { token } = await this.identityApi.getCredentials();

      if (!token) {
        throw new Error('Authentication token not available. Please log in again.');
      }

      const headers = new Headers();
      headers.set('X-User-Token', `Bearer ${token}`);
      return headers;
    } catch (originalError) {
      const authError = new Error('Failed to authenticate request. Please log in again.');
      (authError as Error & { cause?: unknown }).cause = originalError;
      console.error('Auth headers error:', originalError);
      throw authError;
    }
  }

  public async updateSystemAddons(
    environment: Environment,
    systemRef: string,
    addons: string[],
    insightResources?: string[],
  ): Promise<void> {
    const baseUrl = await this.buildBaseUrl(environment);
    const systemName = this.extractSystemName(systemRef);
    const url = `${baseUrl}/systems/${encodeURIComponent(systemName)}/addons`;

    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    const addonsString = addons.join(', ');
    const insightResourcesString = insightResources?.join(', ');
    const command: UpdateSystemAddonsCommand = { 
      addons: addonsString, 
      insightResources: insightResourcesString 
    };

    try {
      const response = await this.fetchApi.fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(command),
      });

      await this.handleResponse<void>(response);
    } catch (err) {
      console.error('Failed to update system addons:', err);
      throw err instanceof Error ? err : new Error('Failed to update system addons.');
    }
  }

  public async getSystemAddons(
    environment: Environment,
    systemRef: string,
  ): Promise<string[]> {
    const baseUrl = await this.buildBaseUrl(environment);
    const systemName = this.extractSystemName(systemRef);
    const url = `${baseUrl}/systems/${encodeURIComponent(systemName)}/addons`;
    const headers = await this.getAuthHeaders();

    try {
      const response = await this.fetchApi.fetch(url, { headers });
      const data = await this.handleResponse<SystemAddonsResponse>(response);

      if (data?.addons) {
        return data.addons.split(',').map(addon => addon.trim()).filter(Boolean);
      }

      return [];
    } catch (err) {
      console.error('Failed to fetch system addons:', err);
      throw err instanceof Error ? err : new Error('Failed to fetch system addons.');
    }
  }
}
